import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import { createOutbox } from "../../functions/reportOutbox";

// Diwali Escape Room is fully self-contained: its own session/user state,
// its own login thunk, its own Bearer token. Deliberately does NOT touch
// anything under src/admin/ (sessionSlice, themeSlice) — this game has no
// admin/superadmin account configured, so nothing here should depend on the
// theme-fetch/admin machinery those files pull in.
const DIWALI_USER_KEY = "diwaliUserData";

const uuid4 = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

function buildToken(user) {
  if (!user?.sessionId || !user?.organizationId) return null;
  return `${user.sessionId}&${user.organizationId}`;
}

// state.time only ever gets set once, from startDiwali's response — the
// backend computes a fresh elapsed time on every /submitDiwaliStage call
// (SEC_TO_TIME(TIMESTAMPDIFF(...))) but never returns it, so state.time
// stays stuck at whatever it was on load (usually "00:00:00") for the rest
// of the session. sendReport() was reading that same stale value, so the
// external report always carried ~0 elapsed time regardless of how long the
// player had actually been playing. This tracks it client-side instead —
// same pattern as modak_main's useElapsedTimer, just inlined here since
// sendReport only needs a reasonably-accurate string, not a live display.
function diwaliTimerKey(sessionId, userId) {
  return sessionId && userId ? `diwaliStart_${sessionId}_${userId}` : null;
}

// Anchors the client timer to the server's elapsed time for this stat row
// (TIMESTAMPDIFF against the row's own start), so a refresh, closed tab or new
// device resumes at the true elapsed time instead of restarting from 00:00:00.
function syncDiwaliTimer(sessionId, userId, elapsedSeconds) {
  const key = diwaliTimerKey(sessionId, userId);
  if (!key) return;
  const elapsed = Number(elapsedSeconds);
  if (Number.isFinite(elapsed) && elapsed >= 0) {
    sessionStorage.setItem(key, String(Date.now() - elapsed * 1000));
  } else if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, String(Date.now()));
  }
}

function getDiwaliElapsedTime(sessionId, userId) {
  const key = diwaliTimerKey(sessionId, userId);
  const start = key ? Number(sessionStorage.getItem(key)) : 0;
  const totalSeconds = start ? Math.max(0, Math.floor((Date.now() - start) / 1000)) : 0;
  const pad = (n) => String(n).padStart(2, "0");
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

// Keeps a freshly-linked reportid in sessionStorage so a refresh mid-game
// resumes with it already known instead of calling sendReport's "add" path
// again and creating a duplicate report.
function persistReportId(reportId) {
  try {
    const stored = JSON.parse(sessionStorage.getItem(DIWALI_USER_KEY) || "{}");
    sessionStorage.setItem(DIWALI_USER_KEY, JSON.stringify({ ...stored, reportId }));
  } catch (_) {
    /* ignore */
  }
}

// login-default.php equivalent: reads the same otp/demo/role/save query
// params the legacy shared login include did, but stores the result under
// its own sessionStorage key instead of the platform-wide "userData" one.
export const loginDiwali = createAsyncThunk(
  "diwali/login",
  async (_, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const demo = queryParams.get("demo");
      const otpParam = queryParams.get("otp");

      // Resume an existing session whenever one is saved AND this visit
      // carries no fresh login info of its own — covers a direct visit or
      // reload of /diwali/game or /diwali/game/poker, which have no query
      // params at all but the player is still mid-playthrough; without
      // this, those pages fall through to requiring a fresh `otp`, which
      // they never have.
      //
      // `?demo` or a fresh `?otp` on the URL means "process THIS login",
      // not "resume whatever was here before" — resuming unconditionally
      // (the previous behavior) meant logging in with a different otp, or
      // revisiting with `?demo`, silently kept using the old cached user:
      // for `?demo` that meant a completed session could never be replayed;
      // for a genuinely different `?otp` it meant a different person's
      // login never even hit the API, they just got dropped into the
      // previous visitor's (possibly already-completed) session.
      const stored = sessionStorage.getItem(DIWALI_USER_KEY);
      if (stored && !demo) {
        const storedUser = JSON.parse(stored);
        // Same otp as the login that created this session (e.g. a refresh of
        // the launch URL) is a resume too — re-posting it would spend a
        // login call (and possibly an already-used otp) for nothing.
        if (!otpParam || storedUser.loginOtp === otpParam) return storedUser;
      }

      const fromMobileApp = queryParams.get("fromMobileApp");

      let userResponseData;
      if (demo) {
        const randomString = "demo" + Math.floor(Math.random() * 10000);
        userResponseData = {
          userId: randomString,
          email: `${randomString}@gmail.com`,
          firstName: randomString,
          lastName: randomString,
          sessionId: "demobypass",
          organizationId: "demo" + Math.floor(Math.random() * 100000),
          gameId: null,
          role: "demobypass",
        };
      } else {
        const otp = otpParam;
        const role = queryParams.get("role");
        if (!otp) return rejectWithValue("OTP is required for login.");

        let apiUrl = process.env.REACT_APP_FIREBASE_LOGIN_API;
        const requestData = { otp };
        let source = "ORG_USER";
        if (role === "GUEST_USER") {
          apiUrl = process.env.REACT_APP_FIREBASE_GUEST_USER_API;
          requestData.role = "GUEST_USER";
          source = "GUEST_USER";
        }

        const clientSecret =
          process.env.REACT_APP_FIREBASE_CLIENT_SECRET || process.env.REACT_APP_CLIENT_SECRET;
        const headers = {
          "client-secret": clientSecret,
          "Content-Type": "application/json",
          "Idempotency-Key": uuid4(),
        };

        const response = await axios.post(apiUrl, requestData, { headers });
        if (response.data.message !== "LOGIN SUCCESS") {
          return rejectWithValue("Login failed");
        }
        response.data.data.role = source;
        userResponseData = response.data.data;
      }

      const userData = {
        userId: userResponseData.userId || userResponseData.id,
        name:
          [userResponseData.firstName, userResponseData.lastName]
            .filter((part) => part && String(part).toLowerCase() !== "null")
            .join(" ")
            .trim() ||
          userResponseData.name ||
          userResponseData.employeeId ||
          "",
        email: userResponseData.email || userResponseData.employeeId || "",
        sessionId: userResponseData.sessionId,
        organizationId: userResponseData.organizationId,
        gameId: userResponseData.gameId,
        role: userResponseData.role,
        reportId: userResponseData.reportId || null,
        // Bearer token for the EXTERNAL reports-server API (sendReport.js) —
        // distinct from this app's own internal token (buildToken() below,
        // sessionId&organizationId), which only authenticates calls to
        // glowstone_backend's own /submitDiwaliStage etc.
        token: userResponseData.token || "",
        fromMobileApp: fromMobileApp || userResponseData.fromMobileApp || null,
        loginOtp: demo ? null : otpParam,
      };

      sessionStorage.setItem(DIWALI_USER_KEY, JSON.stringify(userData));
      return userData;
    } catch (error) {
      const apiMsg = error.response?.data?.message;
      return rejectWithValue(apiMsg || error.message);
    }
  }
);

// Every call to this app's own backend gets a deadline and defensive response
// handling: a half-open connection must not leave the player waiting forever,
// and a gateway error page (HTML, not JSON) must surface as a readable error
// instead of an opaque JSON parse failure.
const REQUEST_TIMEOUT_MS = 15000;

async function postBackend(path, token, body) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    let data = null;
    try {
      data = await res.json();
    } catch {
      /* non-JSON body */
    }
    if (!data || typeof data !== "object") {
      throw new Error(`Server error${res.status ? ` (${res.status})` : ""}. Please try again.`);
    }
    return data;
  } catch (err) {
    if (err && err.name === "AbortError") {
      throw new Error("The request timed out. Please check your connection and try again.");
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

// Page components each dispatch startDiwali on mount; while one request is in
// flight, later callers share its result instead of issuing their own.
let startInFlight = null;

// main.php equivalent: creates (or resumes) this player's stat row.
export const startDiwali = createAsyncThunk(
  "diwali/start",
  (_, thunkApi) => {
    if (!startInFlight) {
      startInFlight = runStartDiwali(thunkApi).finally(() => {
        startInFlight = null;
      });
    }
    return startInFlight;
  }
);

async function runStartDiwali({ getState, dispatch, rejectWithValue }) {
  const { user } = getState().diwali;
  const token = buildToken(user);
  if (!token || !user?.userId) return rejectWithValue("Not logged in");

  try {
    const data = await postBackend("/startDiwaliStat", token, {
      userId: user.userId,
      name: user.name || "",
      email: user.email || "",
      userType: user.role || "",
      reportId: user.reportId || null,
    });
    if (!data.success) return rejectWithValue(data.message || "Failed to start game");
    syncDiwaliTimer(user.sessionId, user.userId, data.elapsedSeconds);

    // Retry any report deliveries left over from a previous visit.
    resumePendingReports({ dispatch, getState });

    // A finished game whose report never got linked (tab closed before the
    // background delivery finished) is re-sent once per page load. The stable
    // per-round idempotency key lets the reports server recognise a repeat.
    if (data.completed && !data.reportid && !completedReportRetried) {
      completedReportRetried = true;
      queueReport({ dispatch, getState, gameId: data.id, round: 5, points: Number(data.points) || 0 });
    }
    return data;
  } catch (err) {
    return rejectWithValue(err.message || "Network error");
  }
}

// ---- External report delivery -------------------------------------------
//
// The game backend is the source of truth for progress, so a stage is saved
// there FIRST and the player moves on immediately. The external reports server
// is told afterwards, in the background, through a durable outbox
// (functions/reportOutbox.js): jobs survive a closed tab and are retried with
// backoff, a circuit breaker and an attempt limit, one at a time and newest
// first, so a slow or down reports service can neither delay a player nor cause
// duplicate reports or a retry storm.
let reportCtx = null; // { dispatch, getState } of the live store
let completedReportRetried = false;

const userKey = (user) => (user ? `${user.sessionId}:${user.userId}` : null);

function localStore() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const reportOutbox = createOutbox({
  storage: localStore(),
  // Only deliver jobs that belong to the player currently signed in.
  canDeliver: (payload) => {
    const user = reportCtx && reportCtx.getState().diwali.user;
    return Boolean(user) && userKey(user) === payload.userKey;
  },
  deliver: (payload) => deliverReport(payload),
});

function queueReport({ dispatch, getState, gameId, round, points }) {
  reportCtx = { dispatch, getState };
  const { user, id } = getState().diwali;
  if (!user) return reportOutbox.whenIdle();
  return reportOutbox.enqueue({
    id: `${userKey(user)}:r${round}`,
    group: userKey(user),
    payload: {
      userKey: userKey(user),
      gameId: gameId || id,
      round,
      points,
      // Captured now: a retry hours later must still report the time at completion.
      time: getDiwaliElapsedTime(user.sessionId, user.userId),
    },
  });
}

// Resolves once the current delivery pass has settled (tests, and a possible
// "flush before leaving" hook).
export function awaitPendingReports() {
  return reportOutbox.whenIdle();
}

// Called after login/resume: retries anything left over from a previous visit.
export function resumePendingReports(ctx) {
  reportCtx = ctx;
  return reportOutbox.drain();
}

// Clears queued deliveries and the retry timer (tests, or when a player signs out).
export function resetReportDelivery() {
  reportOutbox.reset();
  completedReportRetried = false;
}

// Delivery health for diagnostics: { pending, dead, breakerOpen, ... }.
export function getReportOutboxStats() {
  return reportOutbox.stats();
}

// One delivery attempt. Throws when the reports server could not be reached so
// the outbox retries it later; a failed id-link is soft (the next stage save
// also carries the id).
async function deliverReport(payload) {
  const { dispatch, getState } = reportCtx;
  const { user, reportid } = getState().diwali;
  const response = await sendReport({
    sessionId: user.sessionId,
    organizationId: user.organizationId,
    userId: user.userId,
    role: user.role,
    token: user.token,
    gameId: user.gameId,
    name: user.name || "",
    points: payload.points,
    time: payload.time,
    reportId: reportid || undefined,
    idempotencyKey: `diwali:${user.sessionId}:${user.userId}:r${payload.round}`,
  });
  const newReportId = extractReportIdFromResponse(response);
  if (newReportId && newReportId !== reportid) {
    // Remember it right away (memory + sessionStorage) so the next delivery
    // updates this report instead of adding another one.
    dispatch(setReportId(newReportId));
    persistReportId(newReportId);
    try {
      await postBackend("/submitDiwaliStage", buildToken(user), {
        id: payload.gameId,
        points: payload.points,
        round: payload.round,
        reportId: newReportId,
      });
    } catch (err) {
      console.error("Failed to link report id:", err);
    }
  }
}

// One save per round at a time: a double-click or an overlapping retry shares
// the in-flight request instead of firing a second save.
const submitInFlight = new Map();

// submit.php equivalent: persists one stage's completion (round 1-4 = the
// four hotspot puzzles, round 5 = Poker/overall completion).
export const submitDiwaliStage = createAsyncThunk(
  "diwali/submitStage",
  (args, thunkApi) => {
    const { round } = args;
    if (!submitInFlight.has(round)) {
      submitInFlight.set(
        round,
        runSubmitStage(args, thunkApi).finally(() => submitInFlight.delete(round))
      );
    }
    return submitInFlight.get(round);
  }
);

async function runSubmitStage({ points, round }, { getState, dispatch, rejectWithValue }) {
  let state = getState().diwali;
  const token = buildToken(state.user);
  if (!token) return rejectWithValue("Game not started");

  // Self-heal a missing id instead of failing outright: this happens when
  // the startDiwali() call fired on page mount (DiwaliGame.jsx/
  // DiwaliPoker.jsx) hasn't resolved yet, or failed/was interrupted, by
  // the time a stage actually completes — most visibly at the very end of
  // a Poker session, where losing this save meant the whole play-through's
  // progress silently never got recorded, surfaced only as a confusing
  // "Game not started" alert right after the player finished playing.
  if (!state.id) {
    try {
      await dispatch(startDiwali()).unwrap();
    } catch {
      // fall through — the id-missing check below reports the failure
    }
    state = getState().diwali;
  }
  const { id, reportid } = state;
  if (!id) return rejectWithValue("Game not started");

  try {
    // Progress first. A report id learned from an earlier delivery rides along
    // here, so the row is linked even if the dedicated link call was missed.
    const data = await postBackend("/submitDiwaliStage", token, {
      id,
      points,
      round,
      reportId: reportid || null,
    });
    if (!data.success) return rejectWithValue(data.message || "Failed to save progress");
    // The server clamps/keeps points monotonic and returns the stored total.
    const savedPoints = Number.isFinite(Number(data.points)) ? Number(data.points) : points;

    // Tell the reports server in the background; the player does not wait.
    queueReport({ dispatch, getState, round, points: savedPoints });

    return { points: savedPoints, round, reportid: getState().diwali.reportid };
  } catch (err) {
    return rejectWithValue(err.message || "Network error");
  }
}

const initialState = {
  // auth
  authStatus: "idle", // idle | loading | succeeded | failed
  user: null,
  authError: null,
  // game progress (stat row)
  status: "idle", // idle | loading | succeeded | failed
  id: null,
  s1: false,
  s2: false,
  s3: false,
  s4: false,
  s5: false,
  completed: false,
  points: 0,
  time: "00:00:00",
  reportid: null,
  error: null,
};

const diwaliSlice = createSlice({
  name: "diwali",
  initialState,
  reducers: {
    // main.php's `sessionStorage.setItem("points", 2)` — seeds the flat
    // base score once the two intro popups are dismissed.
    setBasePoints: (state, { payload }) => {
      state.points = payload;
    },
    setReportId: (state, { payload }) => {
      state.reportid = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginDiwali.pending, (state) => {
        state.authStatus = "loading";
        state.authError = null;
      })
      .addCase(loginDiwali.fulfilled, (state, { payload }) => {
        state.authStatus = "succeeded";
        state.user = payload;
      })
      .addCase(loginDiwali.rejected, (state, { payload }) => {
        state.authStatus = "failed";
        state.authError = payload || "Login failed";
      })
      .addCase(startDiwali.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(startDiwali.fulfilled, (state, { payload }) => {
        state.status = "succeeded";
        state.id = payload.id;
        state.s1 = Boolean(payload.s1);
        state.s2 = Boolean(payload.s2);
        state.s3 = Boolean(payload.s3);
        state.s4 = Boolean(payload.s4);
        state.s5 = Boolean(payload.s5);
        state.completed = Boolean(payload.completed);
        state.points = Number(payload.points) || 0;
        state.time = payload.time || "00:00:00";
        state.reportid = payload.reportid || null;
      })
      .addCase(startDiwali.rejected, (state, { payload }) => {
        state.status = "failed";
        state.error = payload || "Failed to start game";
      })
      .addCase(submitDiwaliStage.fulfilled, (state, { payload }) => {
        const { points, round, reportid } = payload;
        state.points = points;
        state[`s${round}`] = true;
        if (round === 5) state.completed = true;
        if (reportid) state.reportid = reportid;
      });
  },
});

export const { setBasePoints, setReportId } = diwaliSlice.actions;
export default diwaliSlice.reducer;
